import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, Tag, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API = process.env.REACT_APP_BACKEND_URL;

const BlogListPage = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCat, setSelectedCat] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    fetchPosts();
    fetch(`${API}/api/categories`).then(r => r.json()).then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [search, selectedCat]);

  const fetchPosts = async () => {
    const params = new URLSearchParams();
    params.set('status', 'published');
    if (search) params.set('search', search);
    if (selectedCat) params.set('category', selectedCat);
    const res = await fetch(`${API}/api/posts?${params}`);
    const data = await res.json();
    setPosts(data);
  };

  const getCatName = (id) => categories.find(c => c.id === id)?.name || '';

  return (
    <div className="min-h-screen bg-cream">
      <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      <main className="pt-20 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h1 className="font-serif text-4xl sm:text-5xl text-stone-800 font-bold mb-3" data-testid="blog-title">Blog</h1>
            <p className="text-stone-600">Nasveti, zgodbe in inspiracija za mamice.</p>
          </div>

          {/* Search & Filter */}
          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                type="text" placeholder="Iskanje..." value={search} onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-stone-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-transparent"
                data-testid="blog-search"
              />
            </div>
            <select value={selectedCat} onChange={e => setSelectedCat(e.target.value)}
              className="px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-300" data-testid="blog-filter">
              <option value="">Vse kategorije</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* Posts Grid */}
          {posts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-stone-500 text-lg">Ni najdenih objav.</p>
              <p className="text-stone-400 text-sm mt-1">Kmalu bomo objavili nove vsebine.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {posts.map(post => (
                <Link key={post.id} to={`/blog/${post.slug}`} className="group" data-testid={`blog-post-${post.slug}`}>
                  <article className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-stone-100 h-full flex flex-col">
                    {post.featured_image && (
                      <div className="aspect-[16/9] overflow-hidden">
                        <img src={post.featured_image.startsWith('/api') ? `${API}${post.featured_image}` : post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    )}
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getCatName(post.category_id) && (
                          <span className="text-xs font-semibold text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full">{getCatName(post.category_id)}</span>
                        )}
                        <span className="text-xs text-stone-400 flex items-center gap-1"><Calendar size={12} />{new Date(post.created_at).toLocaleDateString('sl-SI')}</span>
                      </div>
                      <h2 className="font-serif text-xl text-stone-800 font-bold mb-2 group-hover:text-violet-700 transition-colors">{post.title}</h2>
                      <p className="text-stone-600 text-sm leading-relaxed flex-1 line-clamp-3">{post.excerpt}</p>
                      <div className="mt-3 flex items-center gap-1 text-violet-600 text-sm font-medium">
                        Preberi več <ArrowRight size={14} />
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BlogListPage;
